import { useAuthStore } from '@/stores/auth'

export function useApi() {
  const baseURL = import.meta.env.VITE_API_BASE_URL || 'https://api.trama.lat/api/v1'
  const auth = useAuthStore()

  async function getValidToken(): Promise<string | null> {
    try {
      return await auth.getAccessToken()
    } catch {
      return null
    }
  }

  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = await getValidToken()
    // The browser must set the multipart boundary header itself for FormData
    // uploads. Setting Content-Type to application/json would break the upload.
    const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData
    const activeCompanyId = localStorage.getItem('trama_active_company_id')
    const res = await fetch(`${baseURL}${path}`, {
      ...init,
      credentials: 'include',
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(activeCompanyId ? { 'X-Company-Id': activeCompanyId } : {}),
        ...init.headers,
      },
    })

    if (res.status === 401) {
      // Token rejected by backend — clear session and redirect to login
      try {
        await auth.signOut()
      } catch {
        // ignore
      }
      window.location.href = '/?error=session_expired'
      throw new Error('Session expired')
    }

    if (!res.ok) {
      const err: any = new Error(`HTTP ${res.status}`)
      err.status = res.status
      try {
        err.body = await res.json()
      } catch {
        // ignore
      }
      throw err
    }

    if (res.status === 204) {
      return {} as T
    }

    // Attempt to parse JSON, if it's empty, return empty object
    const text = await res.text()
    if (!text) return {} as T
    
    try {
      return JSON.parse(text) as T
    } catch {
      return text as unknown as T
    }
  }

  return {
    get: <T>(path: string, params?: Record<string, unknown>) => {
      const qs = params
        ? '?' +
          new URLSearchParams(
            Object.entries(params).reduce((acc, [k, v]) => {
              if (v !== undefined && v !== null) acc[k] = String(v)
              return acc
            }, {} as Record<string, string>),
          ).toString()
        : ''
      return request<T>(path + qs)
    },
    post: <T>(path: string, body?: unknown, init?: RequestInit) =>
      request<T>(path, { ...(init ?? {}), method: 'POST', body: body ? JSON.stringify(body) : undefined }),
    put: <T>(path: string, body?: unknown) =>
      request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
    patch: <T>(path: string, body?: unknown) =>
      request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
    delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
    /**
     * Multipart upload (e.g. company logo).
     * The browser sets Content-Type + boundary automatically — do NOT
     * set it to application/json, or the multipart body is misparsed.
     */
    upload: <T>(path: string, formData: FormData) =>
      request<T>(path, { method: 'POST', body: formData }),
  }
}
